import * as fetch from "../../common/fetch";

import {
  AttributeMetadataCreateRequestType,
  AttributeMetadataDeleteRequestType,
  AttributeMetadataMoveDataRequestType,
  AttributeBulkUpdateRequestType,
  AttributeBulkUpdatePreviewRequestType
} from "../requests/attributeMetdataRequestTypes";

import {
  AttributeMetadataCreateResponseType,
  AttributeMetadataGetAllResponseType,
  AttributeMetadataGetByIdResponseType,
  AttributeBulkUpdatePreviewResponseType,
  AttributeBulkUpdateCountsResponseType
} from "../responses/attributeMetadataResponseTypes";

const url = process.env.WIB_API_URL;

export async function create(request: AttributeMetadataCreateRequestType) {
  const response = await fetch.post(`${url}/attribute-metadata`, request);

  if (response.ok) {
    const responseJson = await response.json();
    return responseJson as AttributeMetadataCreateResponseType;
  }
}

export async function del(id, body: AttributeMetadataDeleteRequestType) {
  const response = await fetch.del(`${url}/attribute-metadata/${id}`, body);

  return response.ok;
}

export async function getAll() {
  const response = await fetch.get(`${url}/attribute-metadata`);

  if (response.ok) {
    const responseJson = await response.json();
    return responseJson as AttributeMetadataGetAllResponseType;
  }
}

export async function getById(id) {
  const response = await fetch.get(`${url}/attribute-metadata/${id}`);

  if (response.ok) {
    const responseJson = await response.json();
    return responseJson as AttributeMetadataGetByIdResponseType;
  }
}

export async function updateAttribute(
  id,
  label,
  description,
  personalInfo,
  singleValueExtractionPreference?,
  sensitivityClassificationType?,
  sensitivityClassificationLabel?
) {
  const response = await fetch.put(`${url}/attribute-metadata/${id}`, {
    label,
    description,
    personalInfo,
    singleValueExtractionPreference,
    sensitivityClassificationType,
    sensitivityClassificationLabel
  });

  if (response.ok) {
    const responseJson = await response.json();
    return responseJson as AttributeMetadataGetByIdResponseType;
  }
}

export async function moveData(request: AttributeMetadataMoveDataRequestType) {
  const response = await fetch.post(
    `${url}/attribute-metadata/move-data`,
    request
  );

  return response.ok;
}

// The bulk update endpoints answer a 400 with a plain-language explanation of why a
// pattern was rejected, so the body is read back rather than discarded.
async function postBulkUpdate<T>(
  path: string,
  request: unknown,
  fallback: string
): Promise<{ data?: T; error?: string }> {
  const response = await fetch.post(`${url}/attribute-metadata/${path}`, request);

  if (response.ok) {
    const responseJson = await response.json();
    return { data: responseJson as T };
  }

  const message = await response.text();

  return { error: message || fallback };
}

export async function bulkUpdatePreview(
  request: AttributeBulkUpdatePreviewRequestType
): Promise<{ data?: AttributeBulkUpdatePreviewResponseType; error?: string }> {
  return postBulkUpdate<AttributeBulkUpdatePreviewResponseType>(
    "bulk-update/preview",
    request,
    "Preview failed"
  );
}

// separate from the preview because counting scans the whole collection and cannot
// stop early, while the preview's samples stop after ten rows
export async function bulkUpdateCounts(
  request: AttributeBulkUpdatePreviewRequestType
): Promise<{ data?: AttributeBulkUpdateCountsResponseType; error?: string }> {
  return postBulkUpdate<AttributeBulkUpdateCountsResponseType>(
    "bulk-update/counts",
    request,
    "Could not calculate totals"
  );
}

export async function bulkUpdate(
  request: AttributeBulkUpdateRequestType
): Promise<{ ok: boolean; error?: string }> {
  const response = await fetch.post(`${url}/attribute-metadata/bulk-update`, request);

  if (response.ok) {
    return { ok: true };
  }

  const message = await response.text();

  return { ok: false, error: message || "Bulk update failed" };
}
